import { RootState } from '@/lib/redux/store';
import { createSelector } from '@reduxjs/toolkit';

export const selectAppSlice = (state: RootState) => state.app;

export const selectIsTokenVerified = createSelector(
  selectAppSlice,
  (substate) => substate.isTokenVerified
);

export const selectUser = createSelector(selectAppSlice, (substate) => substate.user);

export const selectAppUser = createSelector(
  selectAppSlice,
  (substate) => substate.appUser
);

export const selectLoginId = createSelector(
  selectAppSlice,
  (substate) => substate.loginId
);

export const selectIsUserLoading = createSelector(
  selectAppSlice,
  (substate) => substate.isUserLoading
);

export const selectRootAccounts = createSelector(
  selectAppSlice,
  (substate) => substate.rootAccounts
);

export const selectPrimaryRootAccount = createSelector(
  selectAppSlice,
  (substate) => substate.rootAccounts?.[0]
);

export const selectIsTAndCAccepted = createSelector(
  selectAppSlice,
  (substate) => substate.isTAndCAccepted
);

export const selectAppAccessToken = createSelector(
  selectAppSlice,
  (substate) => substate.appAccessToken
);

export const selectFirebaseAccessToken = createSelector(
  selectAppSlice,
  (substate) => substate.firebaseAccessToken
);

export const selectAccountDetails = createSelector(
  selectAppSlice,
  (substate) => substate.accountDetails
);

export const selectAccountIdentities = createSelector(
  selectAppSlice,
  (substate) => substate.accountIdentities
);

export const selectAssignedUsers = createSelector(
  selectAppSlice,
  (substate) => substate.assignedUsers
);

export const selectStats = createSelector(selectAppSlice, (substate) => substate.stats);

export const selectIsStatsLoading = createSelector(
  selectAppSlice,
  (substate) => substate.isStatsLoading
);

export const selectIsLoggedIn = createSelector(
  selectAppSlice,
  (substate) =>
    !!substate.user && !!substate.appAccessToken && !!substate.firebaseAccessToken
);

export const selectExtraStateItems = createSelector(
  selectAppSlice,
  (substate) => substate.extras
);

// New selectors for multi-account support
export const selectCurrentAccountId = createSelector(
  selectAppSlice,
  (substate) => substate.currentAccountId
);

export const selectAllAccounts = createSelector(
  selectAppSlice,
  (substate) => substate.allAccounts
);

export const selectSubAccountsByRoot = createSelector(
  selectAppSlice,
  (substate) => substate.subAccountsByRoot
);

export const selectCurrentAccountDetails = createSelector(
  selectAppSlice,
  (substate) => substate.accountDetails
);

// Helper selector to get the current account object from allAccounts
export const selectCurrentAccount = createSelector(
  [selectCurrentAccountId, selectAllAccounts],
  (currentAccountId, allAccounts) => {
    if (!currentAccountId || !allAccounts) return null;
    return allAccounts.find((account) => account.account_id === currentAccountId) || null;
  }
);

// Helper selector to determine if user has multiple accounts
export const selectHasMultipleAccounts = createSelector(
  [selectAllAccounts, selectSubAccountsByRoot],
  (allAccounts, subAccountsByRoot) => {
    if (!allAccounts) return false;

    // Check if there are multiple root accounts
    const rootAccounts = allAccounts.filter((account) => account.account_type === 'root');
    if (rootAccounts.length > 1) return true;

    // Check if the single root account has sub accounts
    if (rootAccounts.length === 1) {
      const rootAccountId = rootAccounts[0].account_id;
      const subAccounts = subAccountsByRoot[rootAccountId] || [];
      return subAccounts.length > 0;
    }

    return false;
  }
);

// Helper selector to get total account count for display
export const selectTotalAccountCount = createSelector(
  [selectAllAccounts],
  (allAccounts) => {
    return allAccounts?.length || 0;
  }
);
